<template>
  <app-page class="payout-page" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026): leichter Titel, links Monat und Suche; ohne
         Filter und Gruppierung stehen die Aktionen mit Beschriftung rechts in der Werkzeugleiste (Handy: Icon-Knöpfe im
         Kopf) -->
    <template v-if="handy" #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
      <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
    </template>
    <template #toolbar>
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" placeholder="In Auszahlungen suchen" @input="onSearchInput" />
    </template>
    <template v-if="!handy" #toolbar-end>
      <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
      <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
    </template>

    <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
    <div class="app-grid-fill payout-grid" :class="{ 'payout-grid--auto': handy }">
      <dx-data-grid
        :ref="gridRefName"
        :height="gridHeight"
        :data-source="dataSource"
        :columns="columns"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="true"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :hover-state-enabled="!gridTouch"
        :scrolling="scrolling"
        :element-attr="gridAttr"
        :remote-operations="false"
        @toolbar-preparing="onToolbarPreparing"
        @content-ready="onContentReady"
      >
        <dx-search-panel :visible="false" />

        <dx-editing
          refresh-mode="full"
          :allow-adding="false"
          :allow-updating="false"
          :allow-deleting="false"
          mode="batch"
        />

        <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
        <dx-pager :visible="false" />
        <dx-paging :page-size="10" />
        <dx-export :enabled="false" />

        <template #vehicleRatingTemplate="cell">
          <div>
            <v-rating
              length="5"
              hover
              :value="cell.data.data.classification"
            ></v-rating>
          </div>
        </template>
      </dx-data-grid>
    </div>
    <!-- Fußleiste: Anzahl und Stripe-Guthaben (bisher Kennzahlen über der Tabelle), rechts Seitenwahl und Blättern -->
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span>Unterwegs zur Bank <strong>{{ availableText }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span>Ausstehend <strong>{{ pendingText }}</strong></span>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxPager,
  DxEditing,
  DxSearchPanel,
  DxExport,
} from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import DataSource from "devextreme/data/data_source";
import axios from "axios";
import moment from "moment";
import { saveAs } from "file-saver";
import { formatEuro, formatNumber, plural } from "@/utils/format";

export default {
  components: {
    DxDataGrid,
    DxPager,
    DxPaging,
    DxEditing,
    DxSearchPanel,
    DxExport,
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    summaryCount() {
      return formatNumber(this.summaryRows);
    },
    summaryNoun() {
      return plural(this.summaryRows, "Auszahlung", "Auszahlungen");
    },
    // Stripe-Guthaben wie bisher (Cent → Euro, ohne Daten 0), angezeigt nach R6
    availableText() {
      return formatEuro(this.balance ? this.balance.available[0].amount / 100 : 0);
    },
    pendingText() {
      return formatEuro(this.balance ? this.balance.pending[0].amount / 100 : 0);
    },
  },
  watch: {
    month: function () {
      this.refreshDataGrid();
    },
  },
  data() {
    return {
      balance: null,
      gridRefName: "stripePaymentGrid",
      lastId: null,
      firstId: null,
      pagingDirection: null,
      hasMore: false,
      month: new Date().toISOString().substr(0, 7),
      searchText: "",
      summaryRows: 0,
      gridAttr: {},
      columns: [
        {
          dataField: "amount",
          caption: "Betrag",
          dataType: "number",
          format: "######0.00",
          calculateCellValue: function (row) {
            return row.amount / 100;
          },
        },
        {
          dataField: "currency",
          caption: "Währung",
          calculateCellValue: function (row) {
            return row.currency.toUpperCase();
          },
        },
        {
          dataField: "status",
          caption: "Status",
          calculateCellValue: function (row) {
            switch (row.status) {
              case "paid":
                return "Ausgezahlt";
              default:
                return row.status;
            }
          },
        },
        {
          dataField: "destination",
          caption: "Konto",
          calculateCellValue: function (row) {
            return `Bankkonto ****${row.destination.last4}`;
          },
        },
        {
          dataField: "created",
          caption: "Zeit",
          dataType: "datetime",
          calculateCellValue: function (row) {
            return moment.unix(row.created).format("DD MMM YYYY, HH:mm:ss");
          },
        },
        {
          type: "buttons",
          buttons: [
            {
              hint: "Transaktionsliste herunterladen",
              icon: "download",
              visible: true,
              onClick: (e) => this.downloadTransactions(e),
            },
          ],
        },
      ],
      dataSource: new DataSource({
        load: async () => {
          const data = (
            await axios.get(
              `${this.$store.state.baseUrl}stripepayout?month=${moment(
                this.month + "-01"
              ).format("MM")}&year=${moment(this.month + "-01").format("YYYY")}`
            )
          ).data;
          return data;
        },
      }),
    };
  },
  methods: {
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText || "");
    },
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst zweite Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    onContentReady(e) {
      const grid = e.component;
      this.summaryRows = Math.max(Number(grid.totalCount()) || 0, 0);
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    async downloadTransactions(data) {
      try {
        const response = (
          await axios.get(
            `${this.$store.state.baseUrl}stripeexport?payout_id=${data.row.data.id}`
          )
        ).data;
        const signedUrl = response.signedUrl;
        saveAs(signedUrl);
      } catch (e) {
        console.log(e);
      }
    },
  },
  async mounted() {
    this.balance = (await axios.get(`${this.$store.state.baseUrl}stripebalance`)).data;
  },
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.payout-grid.payout-grid--auto {
  flex: none;
}
</style>
